import {
  Briefcase,
  ChevronRight,
  CircleDot,
  List,
  Settings,
  SquareCheck,
  Tag,
  Target,
  UserRound,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import { Link } from "@tanstack/react-router";

const INTAKE_SETTINGS_PATH = "/settings/intake";

const intakeSubNavLabelClass =
  "[&>span:last-child]:min-w-0 [&>span:last-child]:whitespace-nowrap [&>span:last-child]:overflow-hidden [&>span:last-child]:text-ellipsis";

export const IntakeSubMenu = ({ currentPath }: { currentPath: string }) => {
  const { setOpenMobile } = useSidebar();
  const { hasPagePermission } = useUser();
  const [settingsOpen, setSettingsOpen] = useState(() =>
    currentPath.startsWith(INTAKE_SETTINGS_PATH),
  );
  const prevPathRef = useRef(currentPath);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = currentPath;
    if (
      !prev.startsWith(INTAKE_SETTINGS_PATH) &&
      currentPath.startsWith(INTAKE_SETTINGS_PATH)
    ) {
      setSettingsOpen(true);
    }
  }, [currentPath]);

  const canViewOpportunities = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeOpportunities,
  );
  const canViewLeads = hasPagePermission(PAGE_PERMISSION_KEYS.intakeLeads);
  const canViewLeadSources = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeSettingsLeadSources,
  );
  const canViewIntakeRoles = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeSettingsRoles,
  );
  const canViewIntakeTeams = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeSettingsTeams,
  );
  const canViewIncidentTypes = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeSettingsTypes,
  );
  const canViewIntakeStatuses = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeSettingsStatuses,
  );
  const canViewMatterTags = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeSettingsTags,
  );
  const canViewTaskPlans = hasPagePermission(
    PAGE_PERMISSION_KEYS.intakeSettingsTaskPlans,
  );

  const isOpportunitiesActive =
    currentPath === "/opportunities" ||
    currentPath.startsWith("/opportunities/");
  const isLeadsActive =
    currentPath === "/leads" || currentPath.startsWith("/leads/");

  return (
    <SidebarMenuSub className="mt-1.5 mr-0 ml-2 gap-1 border-l-2 border-l-neutral-900 py-0.5 pr-0 pl-3">
      {canViewOpportunities ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={intakeSubNavLabelClass}>
            <Link
              to="/opportunities"
              className={cn(
                isOpportunitiesActive && "bg-neutral-900 text-white",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <Target className="mr-2 h-4 w-4 shrink-0" />
              Opportunities
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewLeads ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={intakeSubNavLabelClass}>
            <Link
              to="/leads"
              className={cn(isLeadsActive && "bg-neutral-900 text-white")}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <List className="mr-2 h-4 w-4 shrink-0" />
              Leads
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewLeadSources ||
      canViewIntakeRoles ||
      canViewIntakeTeams ||
      canViewIncidentTypes ||
      canViewIntakeStatuses ||
      canViewMatterTags ||
      canViewTaskPlans ? (
        <SidebarMenuSubItem className="list-none p-0">
          <SidebarMenuSubButton
            className={intakeSubNavLabelClass}
            onClick={() => {
              setSettingsOpen((open) => !open);
            }}
          >
            <Settings className="mr-2 h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-left">Settings</span>
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                settingsOpen && "rotate-90",
              )}
              aria-hidden
            />
          </SidebarMenuSubButton>
          {settingsOpen ? (
            <SidebarMenuSub className="mt-0.5 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
              {canViewLeadSources ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={intakeSubNavLabelClass}
                  >
                    <Link
                      to="/settings/intake/lead-sources"
                      className={cn(
                        currentPath === "/settings/intake/lead-sources" &&
                          "bg-neutral-900 text-white",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <Settings className="mr-2 h-4 w-4 shrink-0" />
                      Lead sources
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
              {canViewIntakeRoles ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={intakeSubNavLabelClass}
                  >
                    <Link
                      to="/settings/intake/roles"
                      className={cn(
                        currentPath === "/settings/intake/roles" &&
                          "bg-neutral-900 text-white",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <UserRound className="mr-2 h-4 w-4 shrink-0" />
                      Roles
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
              {canViewIntakeTeams ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={intakeSubNavLabelClass}
                  >
                    <Link
                      to="/settings/intake/teams"
                      className={cn(
                        currentPath === "/settings/intake/teams" &&
                          "bg-neutral-900 text-white",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <Users className="mr-2 h-4 w-4 shrink-0" />
                      Teams
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
              {canViewIncidentTypes ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={intakeSubNavLabelClass}
                  >
                    <Link
                      to="/settings/intake/matter-types"
                      className={cn(
                        currentPath === "/settings/intake/matter-types" &&
                          "bg-neutral-900 text-white",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <Briefcase className="mr-2 h-4 w-4 shrink-0" />
                      Types
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
              {canViewIntakeStatuses ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={intakeSubNavLabelClass}
                  >
                    <Link
                      to="/settings/intake/statuses"
                      className={cn(
                        currentPath === "/settings/intake/statuses" &&
                          "bg-neutral-900 text-white",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <CircleDot className="mr-2 h-4 w-4 shrink-0" />
                      Statuses
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
              {canViewMatterTags ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={intakeSubNavLabelClass}
                  >
                    <Link
                      to="/settings/intake/tags"
                      className={cn(
                        currentPath === "/settings/intake/tags" &&
                          "bg-neutral-900 text-white",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <Tag className="mr-2 h-4 w-4 shrink-0" />
                      Tags
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
              {canViewTaskPlans ? (
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    className={intakeSubNavLabelClass}
                  >
                    <Link
                      to="/settings/intake/task-plans"
                      className={cn(
                        currentPath === "/settings/intake/task-plans" &&
                          "bg-neutral-900 text-white",
                      )}
                      onClick={() => {
                        setOpenMobile(false);
                      }}
                    >
                      <SquareCheck className="mr-2 h-4 w-4 shrink-0" />
                      Task plans
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ) : null}
            </SidebarMenuSub>
          ) : null}
        </SidebarMenuSubItem>
      ) : null}
    </SidebarMenuSub>
  );
};
